import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { OfferService } from './offers.service';

@Component({
    selector: 'app-free-resourse',
    templateUrl: './free-resourse.component.html',
    styleUrls: ['./free-resourse.component.css'],
    standalone: false
})
export class FreeResourseComponent implements OnInit {
@ViewChild('chipsEl') chipsEl: ElementRef;

  allOffers: any[] = [];
  loading: boolean = false;
  error: string = null;
  sub: Subscription;

  categories: string[] = [];
  categoryCounts: { [key: string]: number } = {};
  activeCategory: string = 'all';

  skeletons: number[] = [1, 2, 3, 4, 5, 6];

  private placeholderCache: { [key: string]: SafeUrl } = {};
  private placeholderRawCache: { [key: string]: string } = {};
  private phTones: string[][] = [
    ['#4f46e5', '#3730a3'],
    ['#14171f', '#2a2f3d'],
    ['#4338ca', '#14171f'],
    ['#3730a3', '#4f46e5']
  ];

  constructor(
    private offerService: OfferService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.loadAssets();
    this.loadOffers();
  }

  ngOnDestroy() {
    if (this.sub) { this.sub.unsubscribe(); }
  }

  /* ---------- Fonts & icons (dynamic, with duplicate guards) ---------- */
  private loadAssets() {
    this.injectLink('of-font-sg', 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap', null);
    this.injectLink('of-tabler-icons', 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.19.0/dist/tabler-icons.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/tabler-icons/3.19.0/tabler-icons.min.css');
  }

  private injectLink(id: string, href: string, fallback: string) {
    if (document.getElementById(id)) { return; }
    var link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    if (fallback) {
      link.onerror = function () {
        if (link.href !== fallback) { link.href = fallback; }
      };
    }
    document.head.appendChild(link);
  }

  /* ---------- Data ---------- */
  loadOffers() {
    var self = this;
    self.loading = true;
    self.error = null;

    if (self.sub) { self.sub.unsubscribe(); }

    self.sub = self.offerService.getAllOffers().subscribe(
      function (res: any) {
        var arr: any[] = [];
        if (res && res.data && Array.isArray(res.data)) {
          arr = res.data.slice();
        } else if (Array.isArray(res)) {
          arr = res.slice();
        }

        self.allOffers = arr.reverse();

        self.categories = ['all'];
        self.categoryCounts = { all: self.allOffers.length };
        self.allOffers.forEach(function (o) {
          if (o.category && String(o.category).trim() !== '') {
            var cat = String(o.category).toLowerCase();
            if (self.categories.indexOf(cat) === -1) {
              self.categories.push(cat);
              self.categoryCounts[cat] = 0;
            }
            self.categoryCounts[cat]++;
          }
        });

        if (self.categories.indexOf(self.activeCategory) === -1) {
          self.activeCategory = 'all';
        }
        self.loading = false;
      },
      function (err) {
        console.error('Error loading offers', err);
        self.error = 'Check your connection and try again. If it keeps happening, contact our team.';
        self.loading = false;
      }
    );
  }

  get filteredOffers(): any[] {
    var self = this;
    if (!self.allOffers.length) { return []; }
    if (self.activeCategory === 'all') { return self.allOffers; }
    return self.allOffers.filter(function (o) {
      return (o.category || '').toLowerCase() === self.activeCategory;
    });
  }

  get featuredOffer(): any {
    if (this.allOffers && this.allOffers.length) {
      return this.allOffers[this.allOffers.length - 1];
    }
    return null;
  }

  get resultText(): string {
    var n = this.filteredOffers.length;
    return n + (n === 1 ? ' offer' : ' offers') +
      (this.activeCategory === 'all' ? ' available' : ' in ' + this.activeCategory);
  }

  categoryLabel(c: string): string {
    if (c === 'all') { return 'All offers'; }
    return c.charAt(0).toUpperCase() + c.slice(1);
  }

  setCategory(cat: string, event?: Event) {
    this.activeCategory = cat;
    if (event && event.currentTarget && this.chipsEl) {
      var chip = event.currentTarget as HTMLElement;
      var track = this.chipsEl.nativeElement as HTMLElement;
      var left = chip.offsetLeft - (track.clientWidth / 2) + (chip.clientWidth / 2);
      if (track.scrollTo) {
        track.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        track.scrollLeft = left;
      }
    }
  }

  trackByOffer(index: number, o: any) {
    return (o && (o.id || o._id)) || index;
  }

  /* ---------- Actions ---------- */
  onImageClick(o: any) {
    if (!o) { return; }
    var link = o.page_link || o.pageLink;
    if (!link) { return; }

    if (link.indexOf('/') === 0) {
      this.router.navigateByUrl(link);
    } else {
      window.open(link, '_blank');
    }
  }

  scrollToOffers(event?: Event) {
    if (event) { event.preventDefault(); }
    var el = document.getElementById('offers-list');
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  }

  bookSlot(): void {
    window.location.href = 'https://www.sehyog.co.uk/11plus-help-club/category/workshop/5a96224a84937ec99f53ef8055c4b81d';
  }

  openQuestionBank(): void {
    window.location.href = 'https://www.sehyog.co.uk/11plus-help-club/category/offers';
  }

  /* ---------- Images ---------- */
  imageSrc(o: any): any {
    if (o && o.image) { return o.image; }
    return this.placeholder(o && o.category);
  }

  heroImage(): any {
    return this.imageSrc(this.featuredOffer);
  }

  onImgError(event: Event, label: string) {
    var img = event.target as HTMLImageElement;
    if (!img || img.getAttribute('data-of-failed')) { return; }
    img.setAttribute('data-of-failed', '1');
    img.src = this.placeholderRaw(label);
  }

  // Cached SafeUrl so change detection doesn't re-set src every cycle
  placeholder(label: string): SafeUrl {
    var key = String(label || 'Sehyog offer');
    if (!this.placeholderCache[key]) {
      this.placeholderCache[key] = this.sanitizer.bypassSecurityTrustUrl(this.placeholderRaw(key));
    }
    return this.placeholderCache[key];
  }

  private placeholderRaw(label: string): string {
    var key = String(label || 'Sehyog offer');
    if (this.placeholderRawCache[key]) { return this.placeholderRawCache[key]; }

    var sum = 0;
    for (var i = 0; i < key.length; i++) { sum += key.charCodeAt(i); }
    var t = this.phTones[sum % this.phTones.length];
    var txt = key.replace(/[&<>"']/g, '');
    if (txt.length > 22) { txt = txt.slice(0, 21) + '…'; }

    var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500">' +
      '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="' + t[0] + '"/><stop offset="1" stop-color="' + t[1] + '"/></linearGradient></defs>' +
      '<rect width="800" height="500" fill="url(#g)"/>' +
      '<circle cx="690" cy="90" r="190" fill="#ffffff" fill-opacity="0.07"/>' +
      '<circle cx="90" cy="470" r="140" fill="#F5A623" fill-opacity="0.18"/>' +
      '<rect x="60" y="300" width="64" height="8" rx="4" fill="#F5A623"/>' +
      '<text x="60" y="370" font-family="Space Grotesk, Arial, sans-serif" font-size="46" font-weight="700" fill="#ffffff">' + txt + '</text>' +
      '<text x="60" y="415" font-family="Nunito, Arial, sans-serif" font-size="24" font-weight="700" fill="#ffffff" fill-opacity="0.7">Sehyog Academy</text>' +
      '</svg>';

    var uri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    this.placeholderRawCache[key] = uri;
    return uri;
  }
}